/**
 * FSRS-6 Scheduler — wraps ts-fsrs for 闪念上岸
 */
import { createEmptyCard, fsrs, generatorParameters, Rating, type Card, type FSRS, type Grade, type RecordLogItem } from 'ts-fsrs'
import type { Note, ReviewState } from '@/domain/types'

export type ReviewRating = 1 | 2 | 3 | 4

const RATING_MAP: Record<ReviewRating, Grade> = {
  1: Rating.Again,
  2: Rating.Hard,
  3: Rating.Good,
  4: Rating.Easy,
}

const DEFAULT_PARAMS = generatorParameters({
  request_retention: 0.90,
  maximum_interval: 3650,
  enable_fuzz: true,
  // 关闭短期学习步骤：学习中的卡评"记得"直接按间隔排期（约 3 天+），
  // 避免每 10 分钟就再次到期导致"刚复习完又变待复习"
  enable_short_term: false,
})

let scheduler: FSRS | null = null

function getScheduler(): FSRS {
  if (!scheduler) scheduler = fsrs(DEFAULT_PARAMS)
  return scheduler
}

function noteToCard(note: Note): Card {
  const rs = note.reviewState
  if (rs && rs.algorithmVersion === 'fsrs-6') {
    const card = createEmptyCard(note.createdAtMs ? new Date(note.createdAtMs) : new Date())
    card.state = rs.state as unknown as Card['state']
    card.due = new Date(rs.dueAt)
    card.last_review = rs.lastReviewAt ? new Date(rs.lastReviewAt) : undefined
    card.stability = rs.stability
    card.difficulty = rs.difficulty
    card.elapsed_days = rs.elapsedDays
    card.scheduled_days = rs.scheduledDays
    card.reps = rs.reps
    card.lapses = rs.lapses
    return card
  }
  const card = createEmptyCard(note.createdAtMs ? new Date(note.createdAtMs) : new Date())
  if (note.reviewStage && note.reviewStage > 0) {
    card.state = 2 as unknown as Card['state']
    const intervals = [1, 3, 7, 14, 30, 60, 120]
    const idx = Math.min(note.reviewStage - 1, intervals.length - 1)
    card.stability = intervals[idx]
    card.difficulty = 5
    card.reps = note.reviewStage
    if (note.nextReviewAtMs) card.due = new Date(note.nextReviewAtMs)
    // Elapsed days fix: use due date as fallback for last_review
    card.last_review = note.lastReviewedAtMs
      ? new Date(note.lastReviewedAtMs)
      : (note.nextReviewAtMs ? new Date(note.nextReviewAtMs) : undefined)
  }
  return card
}

function cardToReviewState(card: Card, rating: ReviewRating | null, now: Date): ReviewState {
  return {
    algorithmVersion: 'fsrs-6',
    state: (card.state as unknown as number === 0 ? 'new' : card.state as unknown as number === 1 ? 'learning' : card.state as unknown as number === 2 ? 'review' : 'relearning') as ReviewState['state'],
    dueAt: card.due.toISOString(),
    lastReviewAt: card.last_review?.toISOString() || null,
    stability: card.stability,
    difficulty: card.difficulty,
    elapsedDays: card.elapsed_days,
    scheduledDays: card.scheduled_days,
    reps: card.reps,
    lapses: card.lapses,
    learningStep: 0,
    lastRating: rating,
    suspended: false,
    needsRewrite: false,
    parameterVersion: 'v1',
  }
}

export interface ReviewResult {
  note: Note
  reviewState: ReviewState
  log: {
    noteId: string
    rating: ReviewRating
    reviewedAt: string
    durationMs: number | null
    stateBefore: string
    stateAfter: string
    elapsedDays: number
    scheduledDays: number
  }
  nextIntervals: Record<string, string>
}

export function rateNote(note: Note, rating: ReviewRating, durationMs: number | null = null): ReviewResult {
  const card = noteToCard(note)
  const now = new Date()
  const s = getScheduler()
  const stateBefore = String(card.state)

  const result: RecordLogItem = s.next(card, now, RATING_MAP[rating])
  const newState = cardToReviewState(result.card, rating, now)

  const updatedNote: Note = {
    ...note,
    reviewState: newState,
    reviewCount: (note.reviewCount || 0) + 1,
    nextReviewAtMs: newState.dueAt ? new Date(newState.dueAt).getTime() : null,
    lastReviewedAtMs: now.getTime(),
    updatedAtMs: now.getTime(),
  }

  // Compute preview intervals for all 4 ratings
  const tempCard = noteToCard(note)
  const nextIntervals: Record<string, string> = {}
  for (const r of [1, 2, 3, 4] as ReviewRating[]) {
    const preview = s.next({ ...tempCard }, now, RATING_MAP[r])
    nextIntervals[String(r)] = formatRelative(new Date(preview.card.due), now)
  }

  return {
    note: updatedNote,
    reviewState: newState,
    log: {
      noteId: note.id,
      rating,
      reviewedAt: now.toISOString(),
      durationMs,
      stateBefore,
      stateAfter: String(result.card.state),
      elapsedDays: result.card.elapsed_days,
      scheduledDays: result.card.scheduled_days,
    },
    nextIntervals,
  }
}

export interface ProgressInfo {
  stage: 'new' | 'learning' | 'consolidating' | 'mastered' | 'overdue'
  label: string
  color: string
  progress: number
  nextReviewText: string
  reviewCount: number
}

export function getProgressInfo(note: Note): ProgressInfo {
  const rs = note.reviewState
  const now = new Date()
  const count = note.reviewCount || 0

  if (!rs || rs.state === 'new') {
    return { stage: 'new', label: '未开始', color: '#94A3B8', progress: 0, nextReviewText: '尚未复习', reviewCount: count }
  }

  const dueDate = new Date(rs.dueAt)
  if (dueDate < now) {
    const diffMs = now.getTime() - dueDate.getTime()
    const diffMin = Math.round(diffMs / 60000)
    const diffHours = Math.round(diffMs / 3600000)
    const diffDays = Math.ceil(diffMs / 86400000)
    let overdueText: string
    if (diffMin < 60) overdueText = `已逾期 ${diffMin} 分钟`
    else if (diffHours < 24) overdueText = `已逾期 ${diffHours} 小时`
    else overdueText = `已逾期 ${diffDays} 天`
    return {
      stage: 'overdue', label: overdueText, color: '#EF4444',
      progress: Math.min(1, rs.stability / 30), nextReviewText: overdueText, reviewCount: count,
    }
  }

  if (rs.state === 'learning' || rs.state === 'relearning' || rs.stability < 1) {
    return { stage: 'learning', label: '学习中', color: '#F59E0B', progress: 0.25, nextReviewText: formatRelative(dueDate, now), reviewCount: count }
  }
  if (rs.stability < 14) {
    return { stage: 'consolidating', label: '巩固中', color: '#3B82F6', progress: 0.6, nextReviewText: formatRelative(dueDate, now), reviewCount: count }
  }
  return { stage: 'mastered', label: '稳定', color: '#10B981', progress: 1, nextReviewText: formatRelative(dueDate, now), reviewCount: count }
}

function formatRelative(due: Date, now: Date): string {
  const diffMin = Math.round((due.getTime() - now.getTime()) / 60000)
  if (diffMin <= 0) return '现在'
  if (diffMin < 60) return `${diffMin}分钟后`
  const diffHours = Math.round(diffMin / 60)
  if (diffHours < 24) return `${diffHours}小时后`
  const diffDays = Math.round(diffHours / 24)
  if (diffDays === 1) return '明天'
  return `${diffDays}天后`
}
